// Wiersz rozmowy (patch-50): głowa postaci po lewej, dymek z imieniem, tekstem i dwoma przyciskami odsłuchu (🔊 normalnie, 🐢 wolno) po prawej.
// Wspólny dla trzech ekranów lekcji: scenki (krok 3), „Brakujących słów” (krok 5) i „Co powiesz?” (krok 6). Bez maskotki — mówi postać z dialogu (Tom albo Emma),
// a dziecko gra jedną z nich (chłopiec Toma, dziewczynka Emmę; src/lib/characters.ts `roleFor`). Postać bez głowy (narrator zdań opisu) dostaje neutralną „bańkę”.
import type { Child } from './dom.ts';
import { h, trackPlSlot } from './dom.ts';
import type { Pair } from './i18n.ts';
import { txText } from './i18n.ts';
import type { AppCtx } from './ctx.ts';
import { icon, labelFor } from './kit.ts';

type TextCtx = Pick<AppCtx, 'teacher' | 'account'>;

export type Tone = 'tom' | 'emma' | 'other';

/** Kolor dymka i imienia: Tom — fiolet, Emma — róż (jak na makiecie); ktokolwiek inny — neutralnie. */
export const toneOf = (who: string | undefined): Tone => {
  const n = (who ?? '').toLowerCase();
  return n === 'tom' ? 'tom' : n === 'emma' ? 'emma' : 'other';
};

/** Rysunek głowy postaci (public/chars/<imię>.webp; wycięty z makiety rozmowy). */
export const headSrc = (who: string): string => `./chars/${who.toLowerCase()}.webp`;

let headsWarm = false;
/** Pobiera głowy Toma i Emmy przy starcie lekcji (raz na uruchomienie aplikacji), żeby service worker miał je w pamięci także bez internetu. Poza przeglądarką (testy) nic nie robi. */
export function warmHeads(): void {
  if (headsWarm || typeof Image === 'undefined') return;
  headsWarm = true;
  for (const who of ['Tom', 'Emma']) new Image().src = headSrc(who);
}

const T = {
  me: { en: 'You', pl: 'Ty' },
} as const satisfies Record<string, Pair>;

/** Głowa postaci albo — gdy rysunku nie ma (obcy bohater, plik się nie wczytał) — kółko z inicjałem. */
export function charHead(who: string | undefined): HTMLElement {
  const tone = toneOf(who);
  const head = h('span', { class: `dlg-head tone-${tone}`, 'aria-hidden': 'true', 'data-testid': 'dlg-head', 'data-who': who ?? '' });
  const fallback = (): HTMLElement => h('span', { class: 'dlg-head-fb' }, who ? who.charAt(0).toUpperCase() : '💬');
  if (tone === 'other') {
    head.append(fallback());
    return head;
  }
  const img = h('img', { class: 'dlg-head-img', src: headSrc(who!), alt: '', draggable: 'false', decoding: 'async', 'data-testid': 'dlg-head-img' });
  img.addEventListener('error', () => head.replaceChildren(fallback()));
  head.append(img);
  return head;
}

/** Przycisk odsłuchu w dymku: `run` odtwarza (może zwrócić obietnicę — do końca nagrania wiersz „mówi”). */
export interface LineSound {
  testid: string;
  label: Pair;
  run: () => Promise<unknown> | void;
}

export interface LineOpts {
  /** Mówca (imię postaci); brak = narrator (neutralna bańka, bez imienia). */
  who?: string;
  /** To kwestia roli dziecka: przy imieniu pojawia się „Ty”. */
  me?: boolean;
  /** Treść dymka (tekst, słowa do dotknięcia, zdanie z polem na brakujące słowo). */
  body: Child | Child[];
  /** Tłumaczenie pod tekstem: napis (powstaje ukryty, z zarezerwowanym miejscem) albo gotowy element. */
  pl?: string | HTMLElement;
  testid?: string;
  textTestid?: string;
  plTestid?: string;
  plClass?: string;
  play?: LineSound;
  slow?: LineSound;
}

export interface LineView {
  el: HTMLElement;
  text: HTMLElement;
  pl: HTMLElement | null;
  play: HTMLButtonElement | null;
  slow: HTMLButtonElement | null;
  /** Odsłuch zablokowany (w czasie nagrywania głos z głośnika wpadłby do mikrofonu). */
  setBusy(busy: boolean): void;
  /** Głowa „mówi” (iskry), dopóki trwa obietnica odtwarzania. */
  pulse(p: Promise<unknown> | void): void;
}

export function dialogueLine(ctx: TextCtx, o: LineOpts): LineView {
  const tone = toneOf(o.who);
  const text = h('div', { class: 'dlg-text', 'data-testid': o.textTestid }, ...(Array.isArray(o.body) ? o.body : [o.body]));
  const pl = typeof o.pl === 'string' ? h('p', { class: `dlg-pl pl ${o.plClass ?? ''}`.trim(), 'data-testid': o.plTestid ?? 'dlg-pl', hidden: true }, o.pl) : (o.pl ?? null);
  if (pl && typeof o.pl !== 'string') pl.classList.add('dlg-pl');
  const row = h('div', { class: 'dlg-line', 'data-testid': o.testid ?? 'dlg-line', 'data-who': o.who ?? '', 'data-tone': tone, 'data-me': o.me ? '1' : '0' });
  let pulses = 0;
  const pulse = (p: Promise<unknown> | void): void => {
    row.classList.add('speaking');
    const mine = ++pulses;
    const stop = (): void => {
      if (mine === pulses) row.classList.remove('speaking');
    };
    if (p && typeof (p as Promise<unknown>).then === 'function') (p as Promise<unknown>).then(stop, stop);
    else stop();
  };
  const sound = (s: LineSound | undefined, kind: 'play' | 'slow'): HTMLButtonElement | null => {
    if (!s) return null;
    const b = h('button', { type: 'button', class: `dlg-btn dlg-${kind}`, 'data-testid': s.testid, onclick: () => pulse(s.run()) }, icon(kind === 'play' ? 'speaker' : 'turtle'));
    // 🐢 mówi „wolno”, 🔊 — treść podpisu z `s.label` (np. „Posłuchaj zdania”)
    return labelFor(ctx, b, s.label);
  };
  const play = sound(o.play, 'play');
  const slow = sound(o.slow, 'slow');
  const sounds = play || slow ? h('div', { class: 'dlg-sound' }, play, slow) : null;
  const chip = o.who
    ? h('span', { class: 'dlg-who-row' },
        h('span', { class: 'dlg-who', 'data-testid': 'dlg-who' }, o.who),
        o.me ? txText(ctx, h('span', { class: 'dlg-me', 'data-testid': 'role-tag' }), T.me) : null,
      )
    : null;
  const bubble = h('div', { class: `dlg-bubble${pl ? ' has-pl' : ''}` }, chip, text, pl, sounds);
  if (pl) trackPlSlot(bubble, pl);
  row.append(charHead(o.who), bubble);
  const view: LineView = {
    el: row,
    text,
    pl,
    play,
    slow,
    setBusy(busy) {
      for (const b of [play, slow]) if (b) b.disabled = busy;
    },
    pulse,
  };
  return view;
}
